import React, { FC } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  FormControlLabel,
  Hidden,
  makeStyles,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import Backdrop from "@material-ui/core/Backdrop";
import { useDispatch, useSelector } from "react-redux";
import { BackButton } from "../BackButton";
import { applyFamiglia, applyVoucher, DiscountActionType, setSemestrale } from "../../store/discount/discountReducer";
import { discountSelector } from "../../store/discount/discount.selectors";
import { SimpleSnackbar } from "../SimpleSnackbar";
import { CompleteButton } from "../CompleteButton";
import { setFamiglia } from "../../store/engine/engineReducer";
import { selectionsSelector } from "../../store/engine/engine.selectors";
import { prodottoSelector } from "../../store/prodotto/prodotto.selectors";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 500,
    borderRadius: "15px 0 0 15px",
    backgroundColor: "rgba(103, 162, 173, 0.2)",
    padding: "40px 100px 50px 80px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.only("md")]: {
      padding: "40px 50px 50px 50px",
    },
    [theme.breakpoints.down("sm")]: {
      paddingLeft: "16px",
      paddingRight: "16px",
      borderRadius: 15,
      height: "auto",
      minHeight: "auto",
    },
  },
  boxContainer: {
    display: "flex",
    flexDirection: "column",
  },
  box: {
    marginBottom: 20,
    backgroundColor: "#FFFFFF",
    boxShadow: "0 2px 12px 0 rgba(0,0,0,0.02), 0 10px 30px 0 rgba(0,0,0,0)",
    padding: "40px 37px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    [theme.breakpoints.down("sm")]: {
      paddingLeft: "8%",
      paddingRight: "8%",
    },
  },
  check: {
    padding: 20,
  },
  boxText: {
    fontWeight: 300,
  },
  button: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textAlign: "center",
    width: 192,
    "&:last-child": { marginLeft: 20 },
    "&:first-child": { backgroundColor: "transparent" },
  },
  buttonApplica: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textAlign: "center",
    width: 80,
    borderRadius: 0,
    borderColor: theme.palette.primary.main,
    height: 56, // same as input
    "&:disabled": {
      color: "inherit",
    },
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  discountContainer: {
    marginTop: 20,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  internalInput: {
    color: theme.palette.primary.dark,
  },
  internalBorderInput: {
    borderColor: theme.palette.primary.main,
  },
  outlinedInput: {
    borderColor: theme.palette.primary.main + "!important",
  },
  textFieldRoot: {
    [theme.breakpoints.down("xs")]: {
      width: "60%",
    },
  },
}));

export const Sconto: FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const discount = useSelector(discountSelector);
  const selections = useSelector(selectionsSelector);
  const { data: prodotto } = useSelector(prodottoSelector);
  const { applied, calculating, error, semestrale, voucher } = discount;

  const calcolaPrezzo = (): void => {
    dispatch(applyVoucher());
  };
  const semestraleChangeHandler = (_: React.ChangeEvent<HTMLInputElement>, value: string): void => {
    dispatch(setSemestrale(value === "SI"));
  };
  const famigliaChangeHandler = (_: React.ChangeEvent<HTMLInputElement>, value: string): void => {
    dispatch(setFamiglia(value === "true"));
    dispatch(applyFamiglia());
  };
  const voucherChangeHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch({ type: DiscountActionType.SET_VOUCHER, payload: event.target.value });
  };
  const handleCloseError = (): void => {
    dispatch({ type: DiscountActionType.CLEAR_ERROR });
  };
  return (
    <Fade in>
      <Box className={classes.root}>
        {/* boxContainer is necessary to wrap white boxes and keep them up */}
        <Box className={classes.boxContainer}>
          <Box className={classes.box}>
            <Typography variant={"h2"}>Scegli CHI vuoi assicurare</Typography>
            <Typography className={classes.boxText}>
              Scegliendo &quot;Nucleo Familiare&quot; scegli di assicurare te stesso e tutte le persone risultanti dal
              tuo certificato di stato famiglia. Scegliendo &quot;Single&quot; scegli di assicurare te stesso.
            </Typography>
            <RadioGroup
              row
              aria-label="position"
              name="famiglia"
              value={selections.famiglia}
              onChange={famigliaChangeHandler}
            >
              <FormControlLabel
                value={true}
                control={<Radio style={{ color: "#277A8A" }} />}
                label={
                  <span>
                    <Typography variant={"body2"}>
                      <strong>Nucleo Familiare</strong>
                    </Typography>
                  </span>
                }
                data-test="radio-famiglia-SI"
              />
              <FormControlLabel
                value={false}
                control={<Radio style={{ color: "#277A8A" }} />}
                label={
                  <span>
                    <Typography variant={"body2"}>
                      <strong>Single</strong>
                    </Typography>
                  </span>
                }
                data-test="radio-famiglia-NO"
              />
            </RadioGroup>
          </Box>
          <Box className={classes.box}>
            <Typography variant={"h2"}>Scegli QUANDO pagare</Typography>
            <Typography className={classes.boxText}>
              Scegli se pagare una volta all&apos;anno o in due rate ogni 6 mesi.
            </Typography>
            <RadioGroup
              row
              aria-label="position"
              name="semestrale"
              value={semestrale ? "SI" : "NO"}
              onChange={semestraleChangeHandler}
            >
              <FormControlLabel
                value="NO"
                control={<Radio style={{ color: "#277A8A" }} />}
                label={
                  <span>
                    <Typography variant={"body2"}>
                      <strong>Pagamento annuale</strong>
                    </Typography>
                  </span>
                }
                data-test="radio-NO"
              />
              <FormControlLabel
                value="SI"
                control={<Radio style={{ color: "#277A8A" }} />}
                label={
                  <span>
                    <Typography variant={"body2"}>
                      <strong>Pagamento semestrale</strong>
                    </Typography>
                  </span>
                }
                data-test="radio-SI"
              />
            </RadioGroup>
          </Box>
          {!prodotto?.codiceConvenzione && (
            <Box className={classes.box}>
              <Typography variant={"h2"}>Hai un codice sconto da utilizzare?</Typography>
              <Box className={classes.discountContainer}>
                <TextField
                  id="outlined-helperText"
                  label="Inserisci codice"
                  value={voucher}
                  variant="outlined"
                  color="secondary"
                  classes={{ root: classes.textFieldRoot }}
                  InputLabelProps={{
                    classes: {
                      root: classes.internalInput,
                    },
                  }}
                  InputProps={{
                    classes: {
                      root: classes.internalBorderInput,
                      notchedOutline: classes.outlinedInput,
                    },
                  }}
                  onChange={voucherChangeHandler}
                />
                <Button
                  className={classes.buttonApplica}
                  onClick={calcolaPrezzo}
                  disabled={calculating || applied}
                  data-test="seleziona"
                >
                  Applica
                </Button>
              </Box>
              <SimpleSnackbar message={error} open={!!error} handleClose={handleCloseError} />
            </Box>
          )}
        </Box>
        <Hidden smDown>
          <Box className={classes.footer}>
            <BackButton />
            <CompleteButton />
          </Box>
        </Hidden>
        <Backdrop className={classes.backdrop} open={calculating}>
          <CircularProgress size={100} />
        </Backdrop>
      </Box>
    </Fade>
  );
};
